import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiService } from '@/services/api'
import { Moment } from '@/design-system/feedback'
import { Button, ButtonLink } from '@/design-system/primitives'
import '@/design-system/first-week.css'
import { MOMENT_LEAD, MOMENT_LINK, MOMENT_TITLE } from './firstWeekCopy'

/*
  The magic moment on screen: the page is live and here is what AI agents
  can now read, as three plain sentences. Shown once per page, recorded
  through the tour table so a second device never sees it again either.
  Nothing here may look like code: the server sends sentences, never JSON.
*/
export function LiveMoment({ urlId, url }: { urlId: string; url: string }) {
  const tourKey = `moment-live-${urlId}`
  const queryClient = useQueryClient()
  // Decided once per mount: null while the reads are in flight, then true to
  // show or false to stay quiet. Closing by hand flips it back to false.
  const [open, setOpen] = useState<boolean | null>(null)

  const status = useQuery({
    queryKey: ['tour', tourKey],
    queryFn: () => apiService.getTourStatus(tourKey),
    staleTime: Infinity,
  })
  const seen = status.data?.data?.dismissed

  const facts = useQuery({
    queryKey: ['live-facts', urlId],
    queryFn: () => apiService.getLiveFacts(urlId),
    enabled: seen === false,
    staleTime: Infinity,
  })

  // Mark it seen the moment it is on screen, not when it is closed, so a
  // reload or a second tab does not show it again. The answer goes straight
  // into the cached tour status, which is what a remount in this session
  // reads, so the moment stays quiet there too.
  useEffect(() => {
    if (open !== null || seen !== false || !facts.data) return
    setOpen(true)
    void apiService.dismissTour(tourKey).then((answer) => {
      if (answer?.success) queryClient.setQueryData(['tour', tourKey], answer)
    })
  }, [open, seen, facts.data, tourKey, queryClient])

  if (!open) return null
  const list: string[] = facts.data?.data?.facts ?? []

  return (
    <div className="ds-fw__moment">
      <Moment title={MOMENT_TITLE(url)} detail={list.length ? MOMENT_LEAD : undefined}>
        {list.length > 0 && (
          <ul className="ds-fw__facts">
            {list.map((fact) => (
              <li key={fact}>{fact}</li>
            ))}
          </ul>
        )}
        <div className="ds-fw__moment-foot">
          <ButtonLink variant="secondary" to={url} target="_blank" rel="noreferrer">
            {MOMENT_LINK}
          </ButtonLink>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Close
          </Button>
        </div>
      </Moment>
    </div>
  )
}
